/**
 * "Play a friend."
 *
 * Mint a single-attempt token, share the link, then sit in the duel waiting for
 * them. If they never turn up, spec section 4 says the link plays A's ghost —
 * and the joiner is never told which they got, because invariant 1 means there
 * is nothing to tell.
 *
 * The panel is mode-agnostic: it duels whatever `gameMode` and `courseId` the
 * home screen has selected, and both travel inside the signed token rather than
 * in the URL, so the two ends cannot disagree about what they are playing.
 */

import { useEffect, useState } from 'react';
import { GROUP_MAX_SEATS } from '@geogame/shared';
import type { GameMode, Tier } from '@geogame/shared';
import { courseById } from '@geogame/rules/courses';

import { createDuel } from '../lib/api';
import type { DuelInvite } from '../lib/api';
import { useGame } from '../store/gameStore';

/**
 * What was actually MINTED, captured at mint time and never re-read from the
 * props.
 *
 * The selection above this panel keeps moving after a link exists — the player
 * flips to Cities, picks a different course — and the token cannot follow it.
 * Holding the spec here is what stops the share text from naming a course the
 * link does not carry, and stops "Wait in the duel" from building its match
 * shell out of a mode the token will overrule a moment later.
 */
interface DuelSpec {
  readonly tier: Tier;
  readonly gameMode: GameMode;
  readonly courseId?: string;
  /**
   * What to call it in front of a person: a mode noun, or the course name when
   * one is chosen. Never preceded by "the same" in the copy — half the course
   * names are proper nouns ("The Americas") and the article collides.
   */
  readonly label: string;
  /** Seats the link's lobby holds: 2 for a duel, more for a group. */
  readonly capacity: number;
}

type Phase =
  | { k: 'idle' }
  | { k: 'creating' }
  | { k: 'ready'; invite: DuelInvite; spec: DuelSpec }
  | { k: 'error'; message: string };

/**
 * What the link is a race to do. Used in the share text, which is the only
 * place the recipient learns the mode before they tap.
 */
const MODE_NOUN: Record<GameMode, string> = {
  country: 'countries',
  city: 'cities',
  state: 'US states',
};

export function FriendDuelPanel({
  tier,
  gameMode = 'country',
  courseId = '',
}: {
  readonly tier: Tier;
  readonly gameMode?: GameMode;
  readonly courseId?: string;
}): React.JSX.Element {
  const startMatch = useGame((s) => s.startMatch);
  const [phase, setPhase] = useState<Phase>({ k: 'idle' });

  /*
   * A MINTED LINK DOES NOT SURVIVE A CHANGE TO WHAT IT IS FOR.
   *
   * The link is a signed token: the mode, tier and course are baked into it at
   * mint time and the server obeys the token, not the screen. So a link minted
   * for Countries stayed on display after the player switched to Cities,
   * looking exactly like a Cities link and reading "Open the lobby" — and it
   * opened a Countries game. Reported from production as "created a cities
   * group round and it ended up being country game"; the match it produced ran
   * ESP, GBR, IRL, ETH, CRI, RUS, LKA.
   *
   * Discarding it is the honest answer, because there is nothing to relabel:
   * the token cannot be amended, only replaced. A link already SENT is still a
   * Countries duel and still works, which is correct — it was sent as one. This
   * panel's job is the invitation being made now, and that one always matches
   * the game on screen.
   */
  useEffect(() => {
    setPhase({ k: 'idle' });
  }, [tier, gameMode, courseId]);

  /*
   * A course is a COUNTRY-mode filter, and the server refuses the pair outright
   * rather than dropping it — so the guard is here, not left to the home screen
   * remembering to blank the prop when the mode changes.
   */
  const course = gameMode === 'country' && courseId !== '' ? courseById(courseId) : undefined;
  const label = course !== undefined ? course.name : MODE_NOUN[gameMode];

  /**
   * Mint a link and walk straight into the lobby behind it.
   *
   * ALWAYS THE MAXIMUM, never a choice between two and six. Asking somebody to
   * pick the size of the table is asking them how many friends will turn up,
   * at the one moment nobody can know -- and the answer never mattered: the
   * gateway sizes the match from whoever is actually standing there when
   * somebody presses start. A solo start still races a ghost and still
   * publishes the run back under the token, exactly as a duel link always has.
   *
   * AND IT DOES NOT STOP TO SHOW YOU THE LINK. It used to mint, show a URL and
   * wait for a second tap to enter -- so the link lived on the screen you were
   * about to leave. The lobby is the thing you invite people into, so that is
   * where you land, with the invitation on screen the whole time you are in
   * it.
   */
  const create = (): void => {
    const spec: DuelSpec = {
      tier,
      gameMode,
      ...(course !== undefined ? { courseId: course.id } : {}),
      label,
      capacity: GROUP_MAX_SEATS,
    };
    setPhase({ k: 'creating' });
    createDuel(spec.tier, spec.gameMode, spec.courseId, GROUP_MAX_SEATS)
      .then((invite) => {
        setPhase({ k: 'ready', invite, spec });
        startMatch({
          mode: 'friend',
          tier: spec.tier,
          gameMode: spec.gameMode,
          ...(spec.courseId !== undefined ? { courseId: spec.courseId } : {}),
          shareToken: invite.token,
        });
      })
      .catch((err: unknown) =>
        setPhase({
          k: 'error',
          message: err instanceof Error ? err.message : 'Could not create the link.',
        }),
      );
  };

  /*
   * MOUNTING IS THE DECISION, so there is nothing here to press.
   *
   * This used to render a sentence and one button that said "Open a lobby" --
   * a submenu whose entire content was the thing you had already asked for by
   * opening it. The tap that reveals this panel and the tap that mints the
   * link were the same intention expressed twice.
   *
   * The `phase` machinery below is not dead weight: minting is a network call
   * that can fail, and the difference between "opening" and "that did not
   * work" is the only thing this component has left to say.
   */
  useEffect(() => {
    create();
    // Once, on mount. `create` closes over props that never change while this
    // is mounted -- the home screen unmounts it to cancel rather than
    // reconfiguring it underneath.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase.k === 'error') {
    return (
      <section className="gg-section">
        <p className="gg-note gg-note-error">{phase.message}</p>
        <button type="button" className="gg-btn" onClick={create}>
          Try again
        </button>
      </section>
    );
  }

  return (
    <section className="gg-section">
      <p className="gg-note">Opening a lobby…</p>
    </section>
  );

}
